<template>
  <div class="cooling-stages-form">
    <b-row>
      <b-col xs="12" lg="3">
        <label>
          <span> {{ $t('asset-information.cooling-stages.cooling-stages-amount') }} </span>
          <s-icon
            name="hint"
            v-b-tooltip.hover.right
            :title="$t('asset-information.asset-info.engineering-modules-information')"
          />
        </label>
        <s-input-label-edit
          v-model="coolingStagesAmountProxy"
          :editable="editing"
          :disabled="saving"
        >
          <s-select
            v-model="coolingStagesAmountProxy"
            :options="coolingStagesAmountOptions"
            @input="onChangeCoolingStagesAmount"
          />
        </s-input-label-edit>
      </b-col>
    </b-row>
    <b-row>
      <s-table
        class="cooling-stages-form__table mt-3"
        :fields="fields"
        :items="tableItems"
      >
        <template #thead-top="data">
          <tr class="cooling-stages-form__table-header-top">
            <th
              id="table-gap"
              class="cooling-stages-form__table-header-gap"
              colspan="1"
            >
              <span class="sr-only">Cooling stages names</span>
            </th>
            <th
              id="default"
              colspan="1"
              class="cooling-stages-form__table-header-parameters"
            >
              <span> {{ $t('asset-information.cooling-stages.natural-cooling') }} </span>
            </th>
            <th
              v-if="showDynamicHead(data.columns)"
              id="stages"
              :colspan="getHeadTopDynamicCols(data.columns)"
              class="cooling-stages-form__table-header-parameters"
            >
              {{ $t('asset-information.cooling-stages.cooling-stages') }}
            </th>
          </tr>
        </template>
        <template #cell(name)="item">
          <div class="cooling-stages-form__label-wrapper">
            <strong>
              {{ `${$t(item.item.label)}${item.item.required ? '*' : ''}` }}
              <s-icon
                v-if="showIcon(item.item.name)"
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </strong>
          </div>
        </template>
        <template #cell(stage0)="item">
          <cooling-stages-field
            :key="coolingStagesAmountProxy"
            :stage="0"
            :field="item.item.name"
            :editing="editing"
            :saving="saving"
            :transformer-cooling-stages="value"
            :value="getStagePropertyByStageNumber(item.field.key, item.item.name)"
            @input="setValueOnCoolingObject"
            @update:invalid-field="updateInvalidFields"
          />
        </template>
        <template #cell(stage1)="item">
          <cooling-stages-field
            :key="coolingStagesAmountProxy"
            :stage="1"
            :field="item.item.name"
            :editing="editing"
            :saving="saving"
            :transformer-cooling-stages="value"
            :value="getStagePropertyByStageNumber(item.field.key, item.item.name)"
            @input="setValueOnCoolingObject"
            @update:invalid-field="updateInvalidFields"
          />
        </template>
        <template #cell(stage2)="item">
          <cooling-stages-field
            :key="coolingStagesAmountProxy"
            :stage="2"
            :field="item.item.name"
            :editing="editing"
            :saving="saving"
            :transformer-cooling-stages="value"
            :value="getStagePropertyByStageNumber(item.field.key, item.item.name)"
            @input="setValueOnCoolingObject"
            @update:invalid-field="updateInvalidFields"
          />
        </template>
        <template #cell(stage3)="item">
          <cooling-stages-field
            :key="coolingStagesAmountProxy"
            :stage="3"
            :field="item.item.name"
            :editing="editing"
            :saving="saving"
            :transformer-cooling-stages="value"
            :value="getStagePropertyByStageNumber(item.field.key, item.item.name)"
            @input="setValueOnCoolingObject"
            @update:invalid-field="updateInvalidFields"
          />
        </template>
        <template #cell(stage4)="item">
          <cooling-stages-field
            :key="coolingStagesAmountProxy"
            :stage="4"
            :field="item.item.name"
            :editing="editing"
            :saving="saving"
            :transformer-cooling-stages="value"
            :value="getStagePropertyByStageNumber(item.field.key, item.item.name)"
            @input="setValueOnCoolingObject"
            @update:invalid-field="updateInvalidFields"
          />
        </template>
      </s-table>
    </b-row>
  </div>
</template>

<script>

import coolingStageTableFields from '@/domain/data/installation/assetInformation/coolingStages/coolingStageTableFields';
import coolingStageTableItems from '@/domain/data/installation/assetInformation/coolingStages/coolingStageTableItems';
import CoolingStagesField from '@/domain/components/installation/assetInformation/transformers/coolingStages/CoolingStagesField';
import coolingStageAmountOptions from '@/domain/data/installation/assetInformation/coolingStages/coolingStageAmountOptions';

const stageNumbers = {
  stage0: 0,
  stage1: 1,
  stage2: 2,
  stage3: 3,
  stage4: 4,
};

export default {
  name: 'CoolingStagesForm',
  components: { CoolingStagesField },
  props: {
    value: {
      type: Object,
      default: () => ({
        coolingStagesAmount: 0,
        tertiaryWinding: false,
        monitoredWindingAmount: 0,
        coolingStages: [],
      }),
    },
    saving: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    invalidFields: [],
  }),
  computed: {
    coolingStagesAmountProxy: {
      get() {
        return this.value.coolingStagesAmount || 0;
      },
      set(value) {
        this.$set(this.value, 'coolingStagesAmount', value);
      },
    },
    coolingStagesAmountOptions() {
      return coolingStageAmountOptions;
    },
    fields() {
      const fields = coolingStageTableFields(this);
      return fields.slice(0, this.coolingStagesAmountProxy + 2);
    },
    tableItems() {
      return coolingStageTableItems.map(item => ({
        ...item,
        stage0: this.getStagePropertyByStageNumber(0, item.name),
        stage1: this.getStagePropertyByStageNumber(1, item.name),
        stage2: this.getStagePropertyByStageNumber(2, item.name),
        stage3: this.getStagePropertyByStageNumber(3, item.name),
        stage4: this.getStagePropertyByStageNumber(4, item.name),
      }));
    },
  },
  mounted() {
    this.setColumnsWidth();
  },
  methods: {
    emitInvalidFields() {
      this.$emit('update:invalid-fields', this.invalidFields);
    },
    updateInvalidFields(field) {
      const isFieldVisible = this.coolingStagesAmountProxy >= field.stage;
      if (field.invalid && isFieldVisible) {
        this.invalidFields.push(field);
        this.emitInvalidFields();
        return;
      }
      this.invalidFields = this.invalidFields.filter(item => item.name !== field.name);
      this.emitInvalidFields();
    },
    async setColumnsWidth() {
      await this.$nextTick();
      const widthByStages = {
        1: '25%',
        2: '20%',
        3: '18%',
        4: '16%',
      };
      const width = widthByStages[this.coolingStagesAmountProxy];
      const stages = document.querySelectorAll('.cooling-stages-form__stage') || [];
      stages.forEach((it) => {
        it.width = width;
      });
    },
    onChangeCoolingStagesAmount() {
      this.setColumnsWidth();
      this.invalidFields.forEach((field) => {
        this.updateInvalidFields(field);
      });
    },
    getHeadTopDynamicCols(columns) {
      const defaultColumns = 2;
      return columns - defaultColumns;
    },
    showDynamicHead(columns) {
      const defaultColumns = 2;
      return columns > defaultColumns;
    },
    setValueOnCoolingObject(stage, field, value) {
      this.$emit('change-cooling-object', { stage, field, value });
      this.$forceUpdate();
    },
    getStageKey(stage) {
      return typeof stage === 'string' ? stageNumbers[stage] : stage;
    },
    getStagePropertyByStageNumber(stage, field) {
      const stageByNumber = this.getCoolingStageByStage(stage);
      if (!stageByNumber) return null;
      return stageByNumber[field];
    },
    getCoolingStageByStage(stage) {
      const stages = this.value.coolingStages;
      const stageKey = this.getStageKey(stage);
      return (stages || []).find(item => item.stageNumber === stageKey);
    },
    showIcon(name) {
      if (name === 'windingGradientXOil') {
        return this.value.monitoredWindingAmount >= 2;
      }
      if (name === 'windingGradientYOil') {
        return this.value.monitoredWindingAmount === 3 && this.value.tertiaryWinding;
      }
      return true;
    },
  },
  watch: {
    editing() {
      this.setColumnsWidth();
    },
  },
};

</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .cooling-stages-form
    &__table-header-gap
      background-color: var(--color-background-light)
    &__label-wrapper
      display: flex
      flex-wrap: nowrap
      align-items: center
    ::v-deep
      table
        min-width: 915px
        border-collapse: separate
        border-spacing: .9px
      thead
        tr
          height: 50px
        tr th
          border-bottom: none
        tr th:nth-child(2)
          white-space: normal
          color: $color-primary
        tr th:last-child
          border-right: 1px solid var(--color-table-header-bg)
        tr:last-child th:first-child
          background-color: var(--color-background-light)
        tr:first-child
          color: $color-primary
        tr:first-child th
          white-space: normal
      tbody
        td
          border-top: none
          border-bottom: none
        tr td:first-child
          min-width: 210px
          border-left: 1px solid $color-border-gray !important
        tr td:last-child
          border-right: 1px solid $color-border-gray !important
        tr:first-child td
          border-top: 1px solid $color-border-gray !important
        tr:last-child td
          border-bottom: 1px solid $color-border-gray !important
      th, td
        border: 1px solid var(--color-background-light)
      tr td:last-child, tr th:last-child
        text-align: center !important
  .manage-fonts_18px, .manage-fonts_16px
    .cooling-stages-form
      ::v-deep
        tbody tr td:first-child
          min-width: unset
        th, td
          padding: 0.75rem 0.5rem
</style>
